import ExcelJS from 'exceljs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { getPistasPublicadas, getDatosJornadas } from '../services/asignacionPistaService';
import { ROJO, LILA, rgb, descargarBlob, cabeceraExcel, cabeceraPDF } from './cabeceraFmds';
import { etiquetaWo } from './jornadasUtils';

const COLUMNAS = ['LOCAL', 'VS', 'VISITANTE', 'FASE', 'MODALIDAD', 'PISTA', 'HORA'];
// Jornada pasada: en lugar de pista/hora (una sola columna que ocupa las dos) se muestran los sets.
const COLUMNAS_PASADA = ['LOCAL', 'VS', 'VISITANTE', 'FASE', 'MODALIDAD', 'SETS'];
// Un color de fondo por modalidad, como en el excel antiguo.
export const COLOR_MODALIDAD = {
  'INDIVIDUAL FEMENINO': 'FFD6D0E5',
  'INDIVIDUAL MASCULINO': 'FFD3E4F1',
  'DOBLES FEMENINO': 'FFF5D9D2',
  'DOBLES MASCULINO': 'FFD5E8D4',
  'DOBLES MIXTO': 'FFFFF2CC',
};

const quitarCero = (hora) => (hora ? hora.replace(/^0/, '') : '');
const formatearFecha = (iso) => {
  if (!iso) return '';
  const [a, m, d] = iso.slice(0, 10).split('-');
  return `${d}/${m}/${a}`;
};

// «21-15 · 18-21 · 21-10»; vacío si el partido no tiene sets (no jugado, WO...).
const textoSets = (p) => (p.sets ?? [])
  .slice()
  .sort((a, b) => a.numero - b.numero)
  .map((s) => `${s.puntosLocal}-${s.puntosVisitante}`)
  .join(' · ');

// Pasada = su fecha ya es anterior a hoy; sin fecha, cuando todos sus partidos
// (salvo aplazados) están confirmados.
const esJornadaPasada = (fechaIso, partidos) => {
  if (fechaIso) {
    const hoy = new Date();
    const hoyIso = `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}-${String(hoy.getDate()).padStart(2, '0')}`;
    return fechaIso.slice(0, 10) < hoyIso;
  }
  const jugables = partidos.filter((p) => p.estado !== 'APLAZADO');
  return jugables.length > 0 && jugables.every((p) => p.estado === 'CONFIRMADO');
};

const textoGeneracion = () => {
  const ahora = new Date();
  const fecha = ahora.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const hora = ahora.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  return `Generado el ${fecha} a las ${hora}`;
};

// Agrupa los partidos por jornada y añade pista/hora de la publicación del
// cuadrante (si el ADMIN no la ha publicado, quedan vacías).
const prepararJornadas = async (partidos, temporada) => {
  const numeros = [...new Set(partidos.map((p) => p.jornada))].sort((a, b) => a - b);
  const [publicadas, datos] = await Promise.all([
    Promise.all(numeros.map((n) => getPistasPublicadas(temporada, n).catch(() => null))),
    getDatosJornadas(temporada).catch(() => []),
  ]);
  const datosPorJornada = new Map(datos.map((d) => [d.jornada, d]));
  return numeros.map((numero, i) => {
    const asignacion = new Map((publicadas[i]?.partidos ?? []).map((p) => [p.id, p]));
    const filas = partidos
      .filter((p) => p.jornada === numero)
      .map((p) => {
        const a = asignacion.get(p.id);
        return {
          local: p.local_nombre,
          visitante: p.visitante_nombre,
          fase: [p.arrastradoDe ? `APLAZADO J${p.arrastradoDe}` : (p.tipo ?? ''), etiquetaWo(p)].filter(Boolean).join(' · '),
          modalidad: p.modalidad,
          sets: textoSets(p),
          pista: a?.pista != null ? `PISTA ${a.pista}` : '',
          hora: quitarCero(a?.hora_inicio),
          orden: [a?.hora_inicio ?? '', a?.pista ?? Infinity],
        };
      })
      // Por hora y, a igual hora, por pista; los partidos sin hora ni pista, al final
      .sort((x, y) => {
        const [hx, px] = x.orden;
        const [hy, py] = y.orden;
        if (hx !== hy) return !hx ? 1 : !hy ? -1 : hx.localeCompare(hy);
        return Number(px) - Number(py) || 0;
      });
    const d = datosPorJornada.get(numero);
    const pasada = esJornadaPasada(d?.fecha, partidos.filter((p) => p.jornada === numero));
    return { numero, pasada, fecha: formatearFecha(d?.fecha), sede: (d?.sede ?? '').toUpperCase(), filas };
  });
};

const borde = { style: 'thin', color: { argb: 'FF000000' } };
const bordes = { top: borde, bottom: borde, left: borde, right: borde };

export async function descargarJornadasExcel(partidos, temporada, nombreArchivo) {
  if (!partidos.length) return;
  const jornadas = await prepararJornadas(partidos, temporada);
  const libro = new ExcelJS.Workbook();
  const hoja = libro.addWorksheet('Jornadas', { views: [{ showGridLines: false }] });
  [34, 5, 34, 9, 24, 12, 10].forEach((w, i) => { hoja.getColumn(i + 1).width = w; });

  await cabeceraExcel(libro, hoja, temporada, 7);
  hoja.mergeCells(5, 1, 5, 7);
  const celdaGeneracion = hoja.getCell(5, 1);
  celdaGeneracion.value = textoGeneracion();
  celdaGeneracion.font = { name: 'Arial', size: 8, italic: true };
  celdaGeneracion.alignment = { horizontal: 'center', vertical: 'middle' };

  let fila = 7;
  jornadas.forEach((j) => {
    fila += 1; // fila en blanco entre jornadas
    const titulo = hoja.getRow(fila);
    titulo.height = 22;
    hoja.mergeCells(fila, 1, fila, 2);
    hoja.mergeCells(fila, 3, fila, 4);
    hoja.mergeCells(fila, 5, fila, 6);
    titulo.getCell(1).value = `JORNADA ${j.numero}`;
    titulo.getCell(3).value = j.fecha;
    titulo.getCell(5).value = j.sede;
    for (let c = 1; c <= 7; c += 1) {
      const celda = titulo.getCell(c);
      celda.font = { name: 'Arial', size: 12, bold: true, color: { argb: ROJO } };
      celda.alignment = { horizontal: 'center', vertical: 'middle' };
      celda.border = bordes;
    }
    fila += 1;
    const cab = hoja.getRow(fila);
    const columnas = j.pasada ? COLUMNAS_PASADA : COLUMNAS;
    if (j.pasada) hoja.mergeCells(fila, 6, fila, 7);
    columnas.forEach((nombre, i) => {
      const celda = cab.getCell(i + 1);
      celda.value = nombre;
      celda.font = { name: 'Arial', size: 9, bold: true, color: { argb: ROJO } };
      celda.alignment = { horizontal: 'center', vertical: 'middle' };
      celda.border = bordes;
    });
    j.filas.forEach((f) => {
      fila += 1;
      const r = hoja.getRow(fila);
      r.height = 20;
      if (j.pasada) hoja.mergeCells(fila, 6, fila, 7);
      const valores = j.pasada
        ? [f.local, 'VS', f.visitante, f.fase, f.modalidad, f.sets]
        : [f.local, 'VS', f.visitante, f.fase, f.modalidad, f.pista, f.hora];
      valores.forEach((valor, i) => {
        const celda = r.getCell(i + 1);
        celda.value = valor;
        celda.font = { name: 'Arial', size: 10, bold: i === 1 };
        celda.alignment = { horizontal: i === 0 || i === 2 ? 'left' : 'center', vertical: 'middle' };
        celda.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLOR_MODALIDAD[f.modalidad] ?? LILA } };
        celda.border = { ...bordes, top: { style: 'thin', color: { argb: 'FFB8B0CC' } }, bottom: { style: 'thin', color: { argb: 'FFB8B0CC' } } };
      });
    });
    fila += 1;
  });

  const buffer = await libro.xlsx.writeBuffer();
  descargarBlob(
    new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
    nombreArchivo,
  );
}

export async function descargarJornadasPDF(partidos, temporada, nombreArchivo) {
  if (!partidos.length) return;
  const jornadas = await prepararJornadas(partidos, temporada);
  const doc = new jsPDF({ orientation: 'landscape' });
  let y = await cabeceraPDF(doc, temporada);
  doc.setFont('helvetica', 'italic').setFontSize(8);
  doc.text(textoGeneracion(), doc.internal.pageSize.getWidth() / 2, 40, { align: 'center' });
  jornadas.forEach((j) => {
    autoTable(doc, {
      startY: y,
      margin: { left: 10, right: 10 },
      head: [
        [{ content: [`JORNADA ${j.numero}`, j.fecha, j.sede].filter(Boolean).join('   ·   '), colSpan: 7, styles: { halign: 'center', fontSize: 11 } }],
        j.pasada ? COLUMNAS_PASADA : COLUMNAS,
      ],
      body: j.filas.map((f) => (j.pasada
        ? [f.local, 'VS', f.visitante, f.fase, f.modalidad, f.sets]
        : [f.local, 'VS', f.visitante, f.fase, f.modalidad, f.pista, f.hora])),
      theme: 'grid',
      styles: { fontSize: 8, cellPadding: 2, valign: 'middle', lineColor: [184, 176, 204] },
      headStyles: { fillColor: 255, textColor: rgb(ROJO), fontStyle: 'bold', halign: 'center', lineColor: 0 },
      columnStyles: {
        0: { cellWidth: 62 },
        1: { cellWidth: 10, halign: 'center', fontStyle: 'bold' },
        2: { cellWidth: 62 },
        3: { halign: 'center' },
        4: { halign: 'center' },
        // Sets: ancho fijo para que «21-15 · 18-21 · 21-10» no parta línea
        5: j.pasada ? { halign: 'center', cellWidth: 60, overflow: 'visible' } : { halign: 'center' },
        6: { halign: 'center' },
      },
      didParseCell: (d) => {
        if (d.section === 'body') d.cell.styles.fillColor = rgb(COLOR_MODALIDAD[jornadaModalidad(j, d.row.index)] ?? LILA);
      },
    });
    y = doc.lastAutoTable.finalY + 8;
  });
  doc.save(nombreArchivo);
}

const jornadaModalidad = (j, indice) => j.filas[indice]?.modalidad;
